/* eslint-disable */
import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Paper,
} from "@mui/material";
import {
  FavoriteBorder as HeartOutlineIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { PropertyCard } from "../../Components/PropertyCard";
import { Footer } from "../../Components/Footer";
import { savedPropertyAPI } from "../../services/api";
import { useAuth } from "../../Contexts/AuthContext";
import { useToast } from "../../context/ToastContext";
import { transformProperties } from "../../Utils/propertyTransform";
import { encryptRouteParam } from "../../Utils/encrypt-decrypt";
import MainContentWrapper from "../../Layouts/MainContent/mainContent";

export function SavedProperties() {
  const navigate = useNavigate();
  const { isLoggedIn, user, logout } = useAuth();
  const toast = useToast();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedPropertyIds, setSavedPropertyIds] = useState(new Set());

  useEffect(() => {
    if (isLoggedIn) {
      fetchSavedProperties();
    }
  }, [isLoggedIn]);

  const fetchSavedProperties = async () => {
    try {
      setLoading(true);
      const response = await savedPropertyAPI.getSavedProperties();
      const fetchedProperties = response.data.data.properties || [];

      // Transform properties to ensure consistent data structure
      const transformedProperties = transformProperties(fetchedProperties);
      setProperties(transformedProperties);

      // Create a Set of saved property IDs for efficient lookup
      const ids = new Set(transformedProperties.map((prop) => prop.id));
      setSavedPropertyIds(ids);
    } catch (err) {
      console.error("Error fetching saved properties:", err);
      toast.showError(
        err.response?.data?.message || "Failed to load saved properties"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (property) => {
    const actualPropertyId =
      property.property_id || property.propertyId || property.propertyCode || property.id;
    const encryptedPropertyId = encryptRouteParam(actualPropertyId);
    window.open(`/property/${encryptedPropertyId}`, '_blank', 'noopener,noreferrer');
  };

  const handleSaveToggle = (propertyId, isSaved) => {
    if (!isSaved) {
      // Property was unsaved, remove it from the list
      setProperties((prev) => prev.filter((prop) => prop.id !== propertyId));
      setSavedPropertyIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(propertyId);
        return newSet;
      });
    }
  };

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  const handleHomeClick = () => {
    navigate("/");
  };

  const handleMyPropertiesClick = () => {
    navigate("/my-properties");
  };

  const handleListPropertyClick = () => {
    navigate("/my-properties");
  };

  if (!isLoggedIn) {
    const handleNavigate = () => navigate("/login");
    return (
      <MainContentWrapper>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            py: 8,
            px: { xs: 2, sm: 2, md: '40px', lg: '80px' },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 8,
              textAlign: "center",
              bgcolor: "background.paper",
              borderRadius: 3,
            }}
          >
            <HeartOutlineIcon
              sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h4" gutterBottom>
              Please Login to View Saved Properties
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Sign in to save your favorite properties and access them anytime.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleNavigate}
            >
              Sign In
            </Button>
          </Paper>
        </Container>
      </MainContentWrapper>
    );
  }

  return (
    <MainContentWrapper>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          py: 2,
          px: { xs: 2, sm: 2, md: '40px', lg: '80px' },
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
            Saved Properties
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your favorite properties all in one place
          </Typography>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : properties.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 8,
              textAlign: "center",
              bgcolor: "background.paper",
              borderRadius: 3,
            }}
          >
            <HeartOutlineIcon
              sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              No Saved Properties Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Start exploring properties and save your favorites by clicking the
              heart icon.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              onClick={handleHomeClick}
            >
              Browse Properties
            </Button>
          </Paper>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {properties.length} {properties.length === 1 ? "property" : "properties"} saved
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(5, 1fr)',
                xxl: 'repeat(6, 1fr)'
              },
              gap: 3,
              maxWidth: '100%',
              overflow: 'hidden',
              '& > *': {
                minHeight: 0,
                minWidth: 0
              }
            }}>
              {properties.map((property, index) => (
                <PropertyCard
                  key={`${property.id}-${index}`}
                  property={property}
                  onViewDetails={handleViewDetails}
                  isLoggedIn={isLoggedIn}
                  user={user}
                  initialSavedState={savedPropertyIds.has(property.id)}
                  onSaveToggle={handleSaveToggle}
                />
              ))}
            </Box>
          </>
        )}
      </Container>

      {/* <Footer /> */}
    </MainContentWrapper>
  );
}
